/**
 * The Documents tab — the document drawer (VALE-404).
 *
 * **Opening a document should not cost you the list.** Production's View action
 * hands the file to a new browser tab, which is fine for reading one document
 * and wrong for the job this tab is actually for: going through a matter's
 * paperwork. You lose the list, the browser's own PDF chrome takes over, and
 * coming back means finding your place again. On a matter carrying eighty
 * documents that is eighty round trips.
 *
 * So the page opens it in place, in the app's standard right-side detail panel
 * (`RightDetailSheet` — the same shell discovery and title-search use), with the
 * document itself in the body and the list still behind it: closing it costs
 * nothing, because the list never went anywhere.
 *
 * **Word, Excel and PowerPoint are shown here too** (VALE-900). The preview
 * endpoint converts them to PDF server-side, which is why `canPreviewFile` and
 * `isPreviewableExtension` count them as previewable and why six other surfaces
 * — the discovery file panel, the communications attachment chip, the templates
 * library — already read them inline. This drawer branched on PDF-or-image
 * alone and refused them, which was fine while it sat beside the legacy tab
 * (whose View action opened a browser tab that rendered them) and became a lost
 * capability the moment this became the only Documents tab.
 *
 * A genuinely unconvertible file (`.msg`, an archive) still gets an honest
 * panel — now with the control it names, rather than telling the reader to open
 * a tab the drawer never offered.
 */

'use client';

import { useRef, useState } from 'react';
import {
  ChevronDown,
  Download,
  ExternalLink,
  FileQuestion,
  Loader2
} from 'lucide-react';
import {
  AttributeUnsavedStateProvider,
  focusOpenAttributeEditor
} from '@/components/attributes';
import {
  UnsavedChangesDialog,
  useUnsavedChangesGuard
} from '@/components/shared/unsaved-changes-guard';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { DocumentPreview } from '@/components/ui/document-preview';
import { RightDetailSheet } from '@/components/shared/right-detail-sheet';
import {
  canPreviewFile,
  getFileDownloadUrl,
  getFilePreviewUrl,
  isPreviewableExtension
} from '@/lib/services/files/file-utils';
import { PDF_PAGE_CLASS, PDF_VIEWPORT_CLASS } from '@/lib/utils/pdf-preview';
import {
  DocumentCertifiedBadge,
  isImageFileType
} from '@/components/documents/v2';
import type { DocumentItem } from '@/components/documents/v2';
import { DocumentRowActions } from './document-row-actions';
import type { DocumentRowActionHandlers } from './document-row-actions';
import { DocumentAttributeGrid } from './document-attribute-grid';
import { UnreadableDocumentNotice } from '@/components/documents/core/unreadable-document-notice';
import {
  PrecedentDocumentActions,
  PrecedentProvenancePanel,
  usePrecedentRecord
} from '@/components/documents/precedent-documents';
import { useRegeneratePrecedentDocument } from '@/lib/services/precedent-documents';
import { handleMutationError } from '@/lib/api/direct-client';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { typeLabel } from './labels';

/**
 * **The Sheet stays mounted while it closes, and that is load-bearing** — it is
 * what lets a note commit on the way out (VALE-927).
 *
 * The attribute treatment saves on blur, and Radix's dismissable layer blurs the
 * open editor as it dismisses, so a click outside the drawer commits the field
 * on its way past (`use-attribute-commit.ts`, VALE-349). That only happens if
 * there is still an input in the document to blur. This component used to
 * return `null` the moment `doc` went null, tearing the whole subtree out in the
 * same dispatch — the editor was removed rather than blurred, no blur fired, and
 * an edited note was silently discarded.
 *
 * So `doc` drives `open`, and the body renders the **last** document it held
 * until the close animation finishes. Contacts' drawer has always worked this
 * way; the difference was never in the grid.
 */
export function DocumentDrawer({
  doc,
  isCore = false,
  onOpenChange,
  onDownload,
  onOpenInNewTab,
  matterId,
  onRegenerated,
  ...actions
}: {
  /** The document on screen. `null` closes the drawer. */
  doc: DocumentItem | null;
  /** Whether the open document occupies one of the three core slots. */
  isCore?: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload?: (documentId: number) => void;
  /**
   * Hand the file to a new browser tab — the container's `onView`. Offered for
   * a file this drawer cannot render, which is the one case where the browser's
   * own handler is genuinely the better reader.
   */
  onOpenInNewTab?: (documentId: number) => void;
  /**
   * The matter, so a document Vale generated can show where it came from
   * (VALE-928). Optional because this drawer is a standalone component and the
   * provenance block is additive: without it every document simply draws as it
   * always did.
   */
  matterId?: number;
  /**
   * A regeneration replaced the open document, so the panel must follow it:
   * the file it was showing is archived and gone from the list, and the new
   * one has a new id.
   */
  onRegenerated?: (fileId: number) => void;
} & Omit<DocumentRowActionHandlers, 'onOpen' | 'onDownload'>) {
  // The document to draw. Held past `doc` going null so the closing animation
  // has something to render — and, more importantly, so the open editor is
  // still in the document to receive the blur that commits it.
  // Adjusted during render rather than in an effect — the same idiom
  // `useUnsavedChangesGuard` uses for its own `renderedKey`. An effect would
  // paint the closing frame against the *previous* document before correcting
  // itself, and React re-runs this render immediately without committing the
  // discarded one, so there is nothing to see in between.
  const [shown, setShown] = useState<DocumentItem | null>(doc);
  if (doc && doc !== shown) setShown(doc);

  /**
   * The leave guard, for the three things a blur cannot save (§6b): a field the
   * server refused, a save still in flight past the indicator threshold, and a
   * validation refusal holding the editor open.
   *
   * A clean edit never reaches it — that commit is 0ms old on the way out, under
   * the threshold, so closing over a successful save is silent. The prompt is
   * reserved for an edit that genuinely would be lost.
   */
  const attributeUnsavedRef = useRef(false);
  const unsavedChanges = useUnsavedChangesGuard({
    enabled: true,
    resetKey: doc?.id ?? null,
    open: Boolean(doc),
    onOpenChange
  });

  /** Whether the note's editor is open, so `Escape` can mean "cancel the edit". */
  const editingFieldRef = useRef(false);

  /**
   * **Force the open field's commit-on-blur before an outside click dismisses
   * the drawer** (VALE-60).
   *
   * Keeping the Sheet mounted is necessary but not sufficient. Radix's
   * dismissable layer detects an outside click on `pointerdown`, which fires
   * *before* the browser's own mousedown-driven focus change — so the sheet can
   * dismiss before `blur` ever reaches the open editor, and the note is
   * discarded even though the input was still in the document. The text editors
   * commit from their own `onBlur`, so forcing that blur here, synchronously
   * inside the same `pointerdown` dispatch Radix is still processing, runs the
   * commit before Radix's dismiss check looks at it.
   *
   * Unconditional rather than gated on `editingFieldRef`: blurring whatever has
   * focus is a no-op when nothing is being edited, and this is a modal Sheet, so
   * focus can only be inside it while open.
   */
  /**
   * The record behind this document, when Vale generated it from a precedent.
   *
   * Read here rather than inside the menu because Regenerate is one item among
   * the document's other occasional actions, and `DocumentRowActions` is the
   * generic menu every document row uses — it takes a callback, not a
   * precedent-shaped prop. `null` for every document Vale did not generate.
   */
  const precedentRecord = usePrecedentRecord(matterId, doc?.id);

  /**
   * Regenerate this document, in place.
   *
   * **Nothing is asked and the drawer never closes.** This used to reopen the
   * generate drawer prefilled from the record — the same precedent, the same
   * addressee, the same clauses, waiting to be confirmed — then close it,
   * raise a toast, and leave the reader to find the document again to see what
   * changed. Every one of those steps existed to collect answers the record
   * already stores.
   *
   * So it renders straight away and the drawer follows the result: a
   * regeneration archives the file it replaces and writes a new one, so the
   * open document has a new id and `onRegenerated` retargets the panel at it.
   * Without that the reader would be left looking at an archived file that has
   * just left the list.
   *
   * Written once and shared by the `⋯` item and the blank-fields advisory's own
   * action, because they are one operation.
   */
  const regenerate = useRegeneratePrecedentDocument(matterId ?? 0);
  /**
   * The file a regeneration has just produced, until it is the one on screen.
   *
   * The mutation resolving is not the end of the wait: the new document only
   * reaches this panel once the files list has been refetched and the drawer
   * retargeted. Without this the overlay would lift the instant the request
   * came back and show the *superseded* page for a beat before swapping —
   * which reads as the regeneration having done nothing.
   */
  const [awaitingFileId, setAwaitingFileId] = useState<number | null>(null);
  // Cleared when the new document arrives, and on close — otherwise closing
  // mid-regeneration would leave the overlay armed for the next document
  // opened, which has nothing to do with it.
  if (awaitingFileId !== null && (doc === null || doc.id === awaitingFileId)) {
    setAwaitingFileId(null);
  }
  const regenerating = regenerate.isPending || awaitingFileId !== null;

  const handleRegenerate =
    precedentRecord && precedentRecord.documentTemplateId !== null
      ? async () => {
          try {
            const result = await regenerate.mutateAsync(precedentRecord.id);
            if (result.document.docxFileId !== null) {
              setAwaitingFileId(result.document.docxFileId);
              onRegenerated?.(result.document.docxFileId);
            }
          } catch (error) {
            // The one refusal a lawyer can act on is an exhibit that has left
            // the matter, and the message names it. Surfaced rather than
            // swallowed: a Regenerate that appears to do nothing is worse than
            // one that says why it will not.
            toast.error(handleMutationError(error));
          }
        }
      : undefined;

  const handlePointerDownOutside = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  if (!shown) return null;

  const fileType = shown.mimeType || shown.fileType;
  /*
    **The header says what the document IS; the grid below says everything else.**
    This used to repeat type · size · date, which the grid now carries as three
    labelled rows a few pixels lower — the same three facts twice, the second
    time better. What is left is the one thing the grid does not hold: which of
    the matter's slots this document fills, which is a property of the record's
    place rather than of the file.
  */
  const slot = typeLabel(shown.typeSlug) ?? null;

  /*
    **Where a generated document came from, said in the subtitle.**

    It was a titled block in the body, which made a *property* of the document
    read as a section of it. This is the same line the templates detail drawer
    and the communications attachment drawer put under their titles, and it is
    the one fact about this document that is not in the grid below.

    The document's own name is already the merge of the precedent and the
    addressee, so the subtitle names the *precedent* rather than repeating it:
    what a reader wants here is which of the firm's precedents produced this.
  */
  const detail =
    [
      slot,
      // Just the precedent. The addressee is already in the title (a generated
      // document is named `<precedent> - <addressee>`), and spelling both out
      // again made the subtitle a near-verbatim repeat of the line above it.
      // The precedent name survives here because a lawyer may rename the
      // document, and then the title carries neither.
      precedentRecord ? `Generated from ${precedentRecord.templateName}` : null
    ]
      .filter(Boolean)
      .join(' · ') || null;

  return (
    <>
      <RightDetailSheet
        // Driven, not hardcoded: `open={false}` is what makes Radix dismiss —
        // blurring the open editor on its way out — instead of this component
        // unmounting the editor before it can commit.
        open={Boolean(doc)}
        onOpenChange={unsavedChanges.handleOpenChange}
        contentTestId="document-drawer"
        /* No icon. The file type is already the `Type` row of the grid a few
           pixels below, and a bordered tile in the header spent the widest part
           of the panel restating it. */
        title={
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{shown.name}</span>
            {shown.isCertifiedCopy && <DocumentCertifiedBadge />}
          </span>
        }
        description={detail}
        headerActions={
          <div className="flex items-center gap-1">
            {precedentRecord ? (
              /*
                **Two formats, because this document genuinely is two things.**

                What is stored is a `.docx` — that is what Edit in Word checks
                out — and what the reader is shown is the PDF the download
                endpoint converts it to. Everywhere else in the drawer that
                distinction is deliberately hidden, and this is the one control
                where hiding it costs the reader something: a lawyer sending
                this to a bank wants the PDF, and one revising it away from Word
                wants the `.docx`, and neither should have to know which one
                Vale happens to keep.

                Modal, like every menu inside this Sheet (VALE-1153 — see the
                modality comment in `document-row-actions.tsx`). Nothing here
                opens a Dialog, so the LF-4072 hazard the non-modal default
                answers does not arise at all.
              */
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="xs" density="compact" gap="standard">
                    <Download className="h-3.5 w-3.5" />
                    Download
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* The converting read path — the same bytes the row and this
                      drawer already show, so what saves here is what the reader
                      has been looking at. */}
                  <DropdownMenuItem asChild>
                    <a
                      href={getFilePreviewUrl(shown.id)}
                      download={`${shown.name}.pdf`}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      PDF
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={getFileDownloadUrl(shown.id)} download>
                      <Download className="mr-2 h-4 w-4" />
                      Word (.docx)
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                gap="standard"
                variant="outline"
                size="xs"
                onClick={() => onDownload?.(shown.id)}
              >
                <Download className="h-3.5 w-3.5" />
                Download
              </Button>
            )}

            {/* Edit in Word, for a document Vale generated. A plain button and
                not a menu item, because it is the reason a lawyer opens one of
                these: the merge fills in what Vale knows and Word is where they
                write the paragraph no merge field could supply. Renders nothing
                for every document Vale did not generate. */}
            {matterId !== undefined && (
              <PrecedentDocumentActions matterId={matterId} fileId={shown.id} />
            )}

            {/*
            The same action set the row carries, so a decision taken about the
            document you are reading does not cost you the document. Replace and
            Delete each open a Dialog, and this Sheet has to be out of the way
            first: both modal layers write `pointer-events: none` onto `<body>`
            and the closing one's restore races the opening one's lock, which
            strands the whole page un-clickable (`components/CLAUDE.md`). So the
            drawer closes and the handler fires on the next frame, never in the
            same commit — which is also what lets this menu be modal, as a
            Sheet-nested menu has to be to open at all (VALE-1153). Modality is
            no longer passed in: being inside this Sheet is what decides it
            (`components/ui/page-overlay-scope.tsx`).

            Last in the bar, not in the middle of it: a menu of occasional
            actions belongs at the end of a row of buttons, not between two.
          */}
            <DocumentRowActions
              doc={shown}
              isCore={isCore}
              showViewAndDownload={false}
              onCertifiedCopyChange={actions.onCertifiedCopyChange}
              onUncategorise={actions.onUncategorise}
              onCategorise={actions.onCategorise}
              identityAssignees={actions.identityAssignees}
              onAssignIdentity={actions.onAssignIdentity}
              onRegenerate={handleRegenerate}
              onReplace={
                actions.onReplace &&
                (() => {
                  onOpenChange(false);
                  requestAnimationFrame(() => actions.onReplace?.());
                })
              }
              onDelete={
                actions.onDelete &&
                ((id) => {
                  onOpenChange(false);
                  requestAnimationFrame(() => actions.onDelete?.(id));
                })
              }
            />
          </div>
        }
        /*
        **Two columns, so the page and its record are read side by side.**

        This was 700px and one column: the page, and above it a two-across grid
        the reader scrolled past to reach the document. Which meant the six
        facts about a document and the document itself were never on screen
        together, and every one of them is a fact you check *against* the page.

        So it takes the templates detail drawer's shape — `sm:w-[85vw]`, the
        document on the left, its record in a fixed column on the right — with a
        `max-w` the templates drawer does not need: that one fills its width with
        two content-heavy columns, whereas this one's left column is a single
        page, so past ~1180px it would sit marooned again, which is the defect
        the 700px cap was answering. The page takes that column's full width
        (VALE-1584) — so this `max-w` is what bounds the page, and the shared
        720px cap it used to be bounded by is lifted at the call site below.

        Each column scrolls on its own (`bodyClassName` is a flex row, not a
        scroller) — a long document must not scroll the fields off the screen
        they are there to be compared with.
      */
        className="sm:w-[85vw] sm:max-w-[1180px]"
        bodyClassName="gap-6 overflow-y-auto p-6 lg:flex-row lg:overflow-hidden"
        /*
        Radix owns `Escape`. Without this, one press both reverts the open note
        and dismisses the drawer — so while a field is being edited, `Escape`
        belongs to the editor and the drawer stays put.
      */
        onEscapeKeyDown={(event) => {
          if (editingFieldRef.current) event.preventDefault();
        }}
        onPointerDownOutside={handlePointerDownOutside}
      >
        {/* **The document, on the left and first in the DOM.** It is what the
            reader came for, and putting it first means a keyboard or screen
            reader meets it before the metadata about it. */}
        {/*
          **A flex column at `lg`, so the page below can be given a real height**
          (VALE-1800). This column scrolls (`lg:overflow-y-auto`) and so does the
          document's own pdf.js viewport inside it, and the pair only reads as
          nested once the outer one has something to scroll. It did: the preview
          took an A4-aspect height off this column's ~800px width (~1130px), so
          on any viewport shorter than that the column scrolled its own frame
          beside the scrollbar that moves the document — two adjacent bars, and
          the outer one moved the wrong thing.

          The fix is to make the page fill this column rather than overhang it,
          which needs the height to be DEFINITE the whole way down: a percentage
          height resolves to `auto` against an auto-height ancestor, which is why
          the `h-full` that was already on the preview had never done anything.
          `flex-col` here plus `flex-1 min-h-0` on the wrapper below is that
          chain. `overflow-y-auto` stays rather than becoming `overflow-hidden`:
          with the page fitting there is nothing to scroll and no bar appears,
          and if the notice above is ever tall enough to squeeze it, scrolling is
          the better failure than clipping.

          Below `lg` the columns stack and the BODY scrolls, so none of this
          applies and the page keeps its A4-aspect height.
        */}
        <div className="min-w-0 flex-1 lg:flex lg:flex-col lg:overflow-y-auto">
          {/* The one Documents surface with room for the explanation, so it gets
              the full variant. The preview still renders beneath it — the file
              itself is downloadable and viewable; it is only Vale's *reading* of
              it that failed, and hiding the document would misrepresent that. */}
          <UnreadableDocumentNotice
            reasonCode={shown.analysisFailureReasonCode}
            className="mb-4 lg:shrink-0"
          />
          {/* The document, with the regeneration happening over it rather than
              somewhere else. A regeneration takes a second or two and replaces
              exactly what is on screen, so this is where the wait belongs: the
              reader watches the thing they asked to change.

              `lg:flex-1` plus a min-height is the middle link of the
              definite-height chain described above — a flex item whose
              min-height is `auto` floors at its content's size, which is what
              would let the page overhang the column again.

              The floor is `12rem` rather than `0` so the page cannot be
              squeezed to nothing by the notice above it: measured against a
              notice taller than the whole column, `min-h-0` gave a literal 0px
              document. That notice is far taller than the real banner ever is,
              but 0px is the wrong way to fail. Past the floor the column
              scrolls instead, which is what its `overflow-y-auto` is held
              for. */}
          <div className="relative lg:min-h-[12rem] lg:flex-1">
            <DrawerDocumentPreview
              doc={shown}
              fileType={fileType}
              onOpenInNewTab={onOpenInNewTab}
            />
            {regenerating && (
              <div className="bg-card/70 absolute inset-0 flex items-start justify-center pt-24 backdrop-blur-[1px]">
                <span className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating this document again…
                </span>
              </div>
            )}
          </div>
        </div>

        {/* **The record, in a column beside it.** Fixed width rather than a
            share of the row: these are six short label/value pairs, so a
            proportional column would stretch them on a wide screen and crush
            them on a narrow one. It scrolls on its own, so the note stays
            reachable on a long document. Collapses under the page below `lg`,
            where there is no room for two columns at all. */}
        <div className="shrink-0 lg:w-[19rem] lg:overflow-y-auto">
          {/* The grid reports what it is holding; the guard reads the union.
                One grid here rather than Contacts' several, but the wiring is
                the same and keeping it means a second row costs nothing. */}
          <AttributeUnsavedStateProvider
            onUnsavedChange={(unsaved) => {
              attributeUnsavedRef.current = unsaved;
              unsavedChanges.setIsDirty(unsaved);
            }}
          >
            <DocumentAttributeGrid
              doc={shown}
              // One per row: this column is ~19rem, where a half-width cell
              // is a sliver rather than a column.
              columns={1}
              // Nobody uploaded a generated document and nobody but Vale
              // named it, so `Uploaded` reads `Generated` and the
              // `Original name` row goes.
              isGenerated={precedentRecord !== null}
              onNotesChange={actions.onNotesChange}
              onCertifiedCopyChange={actions.onCertifiedCopyChange}
              onEditingChange={(editing) => {
                editingFieldRef.current = editing;
              }}
            />
          </AttributeUnsavedStateProvider>

          {/* Under the fields, because it is the same kind of thing: what Vale
                knows about this document rather than the document itself.
                Renders nothing for a document Vale did not generate, which is
                nearly all of them. */}
          {matterId !== undefined && (
            <PrecedentProvenancePanel
              matterId={matterId}
              fileId={shown.id}
              onRegenerate={handleRegenerate}
            />
          )}
        </div>
      </RightDetailSheet>

      {/* Raised only for an edit a blur could not save — a refusal, or a save
          still in flight past the indicator threshold. A clean note commits on
          the way out and never reaches this. */}
      <UnsavedChangesDialog
        open={unsavedChanges.showUnsavedDialog}
        onOpenChange={(next) => {
          unsavedChanges.setShowUnsavedDialog(next);
          // *Keep editing* returns the reader to the field they were stopped
          // leaving — Radix has already blurred it and restored focus to the
          // drawer body by now.
          if (!next) focusOpenAttributeEditor();
        }}
        onDiscard={unsavedChanges.handleDiscardChanges}
        itemLabel="note"
      />
    </>
  );
}

/**
 * Whether this document can be read inline.
 *
 * Bound to the two helpers that already own the answer rather than re-listing
 * the types here: `canPreviewFile` keys off the MIME type and
 * `isPreviewableExtension` off the filename extension, and both are kept in step
 * with the backend's convertible-type set (`files/utils/office-document.util.ts`).
 * `mimeType` is optional on a `DocumentItem`, so the extension is the fallback
 * rather than a second opinion.
 */
function isPreviewable(doc: DocumentItem, fileType: string): boolean {
  return doc.mimeType
    ? canPreviewFile(doc.mimeType)
    : isPreviewableExtension(fileType);
}

function DrawerDocumentPreview({
  doc,
  fileType,
  onOpenInNewTab
}: {
  doc: DocumentItem;
  fileType: string;
  onOpenInNewTab?: (documentId: number) => void;
}) {
  const src = getFilePreviewUrl(doc.id);

  if (isImageFileType(fileType)) {
    return (
      /*
        **An image is shown on the same page a PDF is shown on.** Rendered at
        its own dimensions, a photographed document arrives in whatever shape
        the camera produced: a landscape licence sat wide and short where the
        PDF beside it was a tall page, so moving between two documents in the
        same drawer changed the shape of the surface under them. Worse, an
        image taller than it is wide ran past the viewport.

        So the image is laid into `PDF_PAGE_CLASS` — the same A4 sheet, border
        and shadow — and contained inside it. `object-contain` guarantees the
        whole image is visible and that no dimension can exceed the page, at the
        cost of letterboxing anything that is not A4. That trade is the right
        way round: a consistent page that sometimes has margins beats a surface
        that changes shape and occasionally overflows.

        At `lg` the column hands this a definite height (VALE-1800), so the
        sheet is sized off that and lets the aspect ratio compute its WIDTH
        instead — the same A4 page, now guaranteed to fit the column. Keeping
        `w-full` here would keep the ~1130px height that made the column scroll
        beside the document's own scrollbar. `max-w-[720px]` and `mx-auto` still
        cap and centre it.
      */
      <div
        className={cn(PDF_PAGE_CLASS, 'overflow-hidden lg:h-full lg:w-auto')}
      >
        {/* An authenticated streaming endpoint behind the session cookie, not
            an optimisable static asset: `next/image` fetches through its own
            server-side optimiser, which carries no cookie and would 401 on
            every document. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={doc.name}
          className="h-full w-full object-contain"
        />
      </div>
    );
  }

  /*
    Everything else previewable — a PDF, or a Word / Excel / PowerPoint file the
    preview endpoint converts to one on the way out. `DocumentPreview` fetches
    the bytes and branches on the magic bytes itself, so both arrive here as the
    same thing and need no separate branch.

    The shared embedded-preview presentation, not a second set of literals: the
    same mat, and the same page treatment, every other embedded document in the
    app is read on. The document's own pages carry the sheet — this is the
    surface they scroll on.
  */
  if (isPreviewable(doc, fileType)) {
    return (
      <DocumentPreview
        src={src}
        title={doc.name}
        /*
          **The page fills this column, so the shared 720px cap is lifted here**
          (VALE-1584).

          Every other embedded preview sits in a container already narrower than
          `PDF_VIEWPORT_CLASS`'s cap, so the cap never binds and the only inset
          the reader sees is pdf.js's own scrollbar gutter — about 20px a side,
          which is what a document preview looks like across the app. This
          drawer is the one surface wider than the cap: at `sm:max-w-[1180px]`
          less `p-6`, the `gap-6` and the 19rem record column, the page column is
          ~804px, so the cap centred a 720px viewer inside it and added ~42px of
          mat a side ON TOP of pdf.js's. Two stacked insets, and the drawer read
          as more loosely set than every preview beside it.

          `letter-preview-pane` met the same thing and raised its own cap to
          820px; this goes the last step and lets the viewer take the column,
          because the column is already bounded by the drawer's `max-w` — which
          is what that cap was protecting against in the first place. The comment
          on `className` above is the other half of this: 1180px is chosen so the
          page is never marooned, and it now sizes the page rather than the mat
          around it.
        */
        /*
          **This drawer alone sits its page flush** (VALE-1584). pdf.js reserves
          40px for a scrollbar its scroll container has already accounted for,
          which every other preview absorbs into its own `p-3` mat. Here the
          page IS the column, so the same reserve read as the document floating
          away from its frame. The other five surfaces keep pdf.js's fit.
        */
        /*
          **At `lg` the viewport fills the column instead of taking an A4
          height** (VALE-1800). `PDF_VIEWPORT_CLASS` carries `aspect-[1/1.41]`
          to give the viewport a height at all, which is right on every surface
          that sits in a scrolling page and wrong on the one surface that IS a
          column: off this drawer's ~800px it computes ~1130px, so the column
          had to scroll it and the reader got that bar next to pdf.js's own.

          `lg:aspect-auto lg:h-full` swaps the derived height for the column's
          real one, so the pages scroll inside pdf.js's viewport — the bar that
          actually moves the document — and the column has nothing left to
          scroll. This is also what finally makes `h-full` mean something here:
          it was on this element before and was inert, because the wrapper above
          was auto-height (see the column's comment).

          Below `lg` the aspect ratio still governs: the body scrolls the
          stacked columns, so a viewport filling the column would have no height
          to fill.
        */
        fit="flush"
        className={cn(
          PDF_VIEWPORT_CLASS,
          'max-w-none lg:aspect-auto lg:h-full'
        )}
      />
    );
  }

  return (
    <div className="border-border-strong bg-card mx-auto flex max-w-md flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center">
      <span className="border-border flex h-10 w-10 items-center justify-center rounded-lg border">
        <FileQuestion className="text-muted-foreground h-4 w-4" />
      </span>
      <div>
        <p className="text-foreground text-sm font-semibold">
          This one cannot be shown here
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          Vale cannot render this file type in the app. Open it in a new tab or
          download it to read this document.
        </p>
      </div>
      {/* The control the copy above names. Its absence was the whole complaint:
          the panel told the reader to open a new tab and the drawer offered no
          way to do it. */}
      {onOpenInNewTab && (
        <Button
          gap="standard"
          variant="outline"
          size="xs"
          onClick={() => onOpenInNewTab(doc.id)}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open in a new tab
        </Button>
      )}
    </div>
  );
}
